import {
  API_CREDENTIALS,
  AUDIT_LOGS,
  CHANGE_PASSWORD,
  COMPANY_PROFILE,
  DASHBOARD,
  EDIT_COMPANY_PROFILE,
  EDIT_PROFILE,
  INVOICE_DETAILS,
  INVOICE_LIST,
  MENU_EDITOR,
  PAYMENT_STATUS,
  PRODUCT_DETAILS,
  ROLES,
  ROLE_FEATURE,
  USERS,
  USER_DETAILS,
  USER_FEATURE,
  USER_PROFILE,
} from "@constants/internal-route.constant";
import { ROUTE_CODES } from "@constants/route-codes.constant";
import { IAppRoutes } from "@interface/common.interface";
import { ENV } from "config/ENV.config";
import { lazy } from "react";

// Invoices and the payment gateway's return page. Left out entirely when billing is off, so their
// URLs answer "Page not found" rather than a screen this install does not use.
const BillingRoutes: IAppRoutes[] = [
  {
    link: PAYMENT_STATUS,
    element: lazy(
      () => import("@modules/billing/invoiceList/components/PaymentStatus")
    ),
  },
  {
    link: INVOICE_LIST,
    element: lazy(() => import("@modules/billing/invoiceList")),
    routeCode: ROUTE_CODES.INVOICE_LIST,
  },
  {
    link: INVOICE_DETAILS,
    element: lazy(() => import("@modules/billing/invoice")),
    routeCode: ROUTE_CODES.INVOICE_LIST,
  },
];

export const AppRouteList: IAppRoutes[] = [
  {
    link: USERS,
    element: lazy(() => import("@modules/acl/User")),
    routeCode: ROUTE_CODES.USER,
  },
  {
    // Same grant as the list: anyone who may see the users may open one.
    link: USER_DETAILS,
    element: lazy(() => import("@modules/acl/User/Details")),
    routeCode: ROUTE_CODES.USER,
  },
  {
    link: ROLES,
    element: lazy(() => import("@modules/acl/Role")),
    routeCode: ROUTE_CODES.ROLES,
  },
  {
    link: ROLE_FEATURE,
    element: lazy(() => import("@modules/acl/RoleFeature")),
    routeCode: ROUTE_CODES.ROLE_FEATURE_MAP,
  },
  {
    link: USER_FEATURE,
    element: lazy(() => import("@modules/acl/UserFeature")),
    routeCode: ROUTE_CODES.USER_FEATURE_MAP,
  },
  {
    // auth.features has no company_id, so a menu edit re-labels the app for EVERY company
    // on this deployment. Admin-only rather than a grantable feature, and the save endpoint
    // enforces the same rule server-side.
    link: MENU_EDITOR,
    element: lazy(() => import("@modules/acl/Menu")),
    adminOnly: true,
  },
  {
    // Issuing a credential grants a company-wide machine account that bypasses role
    // checks, so this is admin-only rather than a grantable feature.
    link: API_CREDENTIALS,
    element: lazy(() => import("@modules/acl/ApiCredential")),
    adminOnly: true,
  },
  {
    // Every audited change the company made, with before/after values (payroll included), so
    // admin-only, like the server (AuditLogService). Not a grantable feature.
    link: AUDIT_LOGS,
    element: lazy(() => import("@modules/audit/AuditLog")),
    adminOnly: true,
  },
  {
    link: DASHBOARD,
    element: lazy(() => import("pages/Dashboard")),
  },
  {
    link: COMPANY_PROFILE,
    element: lazy(() => import("pages/CompanyProfile")),
    adminOnly: true,
  },
  {
    link: EDIT_COMPANY_PROFILE,
    element: lazy(() => import("pages/EditCompanyProfile")),
    adminOnly: true,
  },
  {
    link: CHANGE_PASSWORD,
    element: lazy(() => import("pages/ChangePassword")),
  },
  {
    link: USER_PROFILE,
    element: lazy(() => import("pages/Profile")),
  },
  {
    link: EDIT_PROFILE,
    element: lazy(() => import("pages/EditProfile")),
  },
  {
    link: PRODUCT_DETAILS,
    element: lazy(() => import("pages/ProductDetails")),
  },
  ...(ENV.billingEnabled ? BillingRoutes : []),
];
